import {type Directive, type DirectiveBinding, type VNode} from 'vue';
import {unwatchResize, watchResize} from '../library/onResize';

interface StickyOptions
{
    position: string;
    offset: number;
    active: boolean;
    withinParent: boolean;
}

interface StickyData
{
    top: number;
    left: number;
    height: number;
    width: number;
    margin: string;
    popupElement: Nullable<HTMLDivElement>;
    popupElementTopOffset: number;
    parentTop: number;
    parentBottom: number;
}

let supportSticky: boolean = false;

let externalScopeElement = null;

let stickies: Sticky[] = [];

const DEFAULT_OFFSET = 25;

function create(element: HTMLElement, directiveOptions: DirectiveBinding, vNode: VNode)
{
    let options: StickyOptions = Sticky.parseOptions(directiveOptions.value);

    if('top' === options.position && options.offset === DEFAULT_OFFSET)
    {
        let pageHeader = document.getElementById('page-header');

        if (pageHeader)
        {
            options.offset = pageHeader.offsetHeight;
        }
    }

    if('bottom' === options.position)
    {
        // add bottom bar (telephone/dev) offset, we also assume there is only one
        if (document.getElementsByClassName('intranet-bottom-toolbar').length > 0)
        {
            options.offset += document.getElementsByClassName('intranet-bottom-toolbar')[0].clientHeight;
        }

    }

    let enabled = element.getAttribute('data-sticky-enabled') || '0';
    if (!options.active && parseInt(enabled))
    {
        destroy(element);
        return;
    }
    else if (!options.active || parseInt(enabled))
    {
        return;
    }

    if (!externalScopeElement)
    {
        externalScopeElement = document.querySelector('.portuna-application-external-scope');
    }

    new Sticky(element, options, vNode);
}

function destroy(element: HTMLElement)
{
    for (let sticky of stickies)
    {
        if (sticky.element === element)
        {
            sticky.destroy();
        }
    }
}

function getData(element: HTMLElement, top: boolean): StickyData
{
    let computedStyle = window.getComputedStyle(element, null);
    let data          = {
        top: element.offsetTop + (!top ? 20 : 0) /* default margin */,
        left: element.offsetLeft,
        height: element.clientHeight,
        width: element.clientWidth,
        margin: computedStyle.margin,
        popupElement: null,
        popupElementTopOffset: 0,
        parentTop: 0,
        parentBottom: 0,
    };

    let inPopup = externalScopeElement?.contains(element);
    let parent = element.offsetParent as HTMLElement;
    let parentCount = 0;

    while (parent)
    {
        let computedStyle = window.getComputedStyle(parent, null);

        let offsetTop = parent.offsetTop + (!top || 'body' !== parent.nodeName.toLowerCase() ? parseInt(computedStyle.getPropertyValue('padding-top')) : 0);

        data.top  += offsetTop;
        data.left += parent.offsetLeft;

        if (parentCount)
        {
            data.parentTop    += offsetTop;
            data.parentBottom += offsetTop;
        }
        else
        {
            data.parentTop    = offsetTop;
            data.parentBottom = offsetTop + parent.clientHeight;
        }

        if (data.popupElement)
        {
            data.popupElementTopOffset += offsetTop;
        }

        if (inPopup && !data.popupElement && parent.classList.contains('popup-body'))
        {
            data.popupElement = parent;
            data.popupElementTopOffset = parseInt(computedStyle.getPropertyValue('padding-top')) + parseInt(computedStyle.getPropertyValue('padding-bottom'));
        }

        parent = parent.offsetParent as HTMLElement;
        parentCount++;
    }

    return data;
}

function isInViewPort(data: StickyData, top: boolean, withinParent: boolean)
{
    let elementTop     = data.top;
    let elementBottom  = elementTop + data.height;
    let viewportTop    = (data.popupElement ? data.popupElement.scrollTop + data.popupElementTopOffset : window.scrollY || document.documentElement.scrollTop) + (!top ? 20 : 100) /* default margin */;
    let viewportBottom = viewportTop + (data.popupElement ? data.popupElement.clientHeight : window.innerHeight);

    const inViewPort = top
        ? elementTop > viewportTop /*&& elementBottom < viewportBottom*/
        : elementBottom > viewportTop && elementTop < viewportBottom;

    if (!withinParent || inViewPort)
    {
        return inViewPort;
    }

    if (top)
    {
        const topInParent = data.parentTop <= viewportTop && viewportTop <= data.parentBottom;

        return !topInParent;
    }

    const bottomInParent = data.parentTop <= viewportBottom && viewportBottom <= data.parentBottom;

    return !bottomInParent;
}

class Sticky
{
    data: StickyData;

    element: HTMLElement;
    wrapper: Nullable<HTMLDivElement> = null;

    vNode: VNode;

    options: StickyOptions;

    scrollWatcher: any;

    resizeWatcher: PortUNAResizeObserver;

    state: 'default' | 'sticky' = 'default';

    // rebuild: Nullable<VoidFunction>;

    static parseOptions(options: boolean | string | {}): StickyOptions
    {
        if ('string' === typeof options)
        {
            options = {
                position: options,
            };
        }
        else if ('boolean' === typeof options)
        {
            options = {
                active: options,
            };
        }

        return Object.assign({
            position: 'bottom',
            offset: DEFAULT_OFFSET,
            active: true,
            withinParent: false,
        }, options);
    }

    constructor(element: HTMLElement, options: StickyOptions, vNode: VNode)
    {
        this.options = options;
        this.element = element;
        this.vNode   = vNode;

        this.init();
    }

    init()
    {
        if('1' === this.element.getAttribute('data-sticky-enabled'))
        {
            return;
        }
        this.element.setAttribute('data-sticky-enabled', '1');
        // browser support sticky -> set style an abort
        // TODO: margin errors, parent relative -> failure
        if (false && supportSticky)
        {
            this.element.style.position               = 'sticky';
            this.element.style[this.options.position] = this.options.offset + 'px';
        }
        // otherwise use js calculation ...
        else
        {
            let top     = 'top' === this.options.position;
            let wrapper = document.createElement('div');
            this.data   = getData(this.element, top);

            this.wrapper = wrapper;

            if (this.data.height)
            {
                wrapper.style.height = this.data.height + 'px';
            }

            wrapper.style.width  = this.data.width + 'px';
            wrapper.style.margin = this.data.margin;

            (<HTMLElement>this.element.parentNode).insertBefore(wrapper, this.element);
            wrapper.appendChild(this.element);

            this.scrollWatcher = () =>
            {
                let inViewPort = isInViewPort(this.data, top, this.options.withinParent);

                if (inViewPort && 'default' !== this.state)
                {
                    this.reset();
                }
                else if (!inViewPort && 'default' === this.state)
                {
                    this.state                                = 'sticky';
                    this.element.style.position               = 'fixed';
                    this.element.style[this.options.position] = this.options.offset + 'px';
                    this.element.style.width                  = this.data.width + 'px';
                    this.element.classList.add('sticky-floating');
                }
            };

            if (this.data.popupElement)
            {
                this.resizeWatcher = watchResize(this.data.popupElement, () =>
                {
                    this.reset();
                    this.scrollWatcher();
                });

                this.data.popupElement.addEventListener('scroll', this.scrollWatcher);
            }
            else
            {
                this.resizeWatcher = watchResize(document.body, () =>
                {
                    this.reset();
                    this.scrollWatcher();
                });

                window.addEventListener('scroll', this.scrollWatcher);
            }

            stickies.push(this);

            this.scrollWatcher();

            // rebuild currently unused -> but maybe required later?
            //+ was a test, but fixed with other reset handling
            // this.rebuild = () => {
            //     this.destroy();
            //     Vue.nextTick(() => this.init())
            // }
            // this.vNode.context?.$on('v-sticky:rebuild', this.rebuild);
        }
    }

    reset(resetData = true)
    {
        this.state                                = 'default';
        this.element.style.position               = 'static';
        this.element.style[this.options.position] = 'auto';
        this.element.style.removeProperty('width');
        this.element.classList.remove('sticky-floating');

        if(this.wrapper)
        {
            this.wrapper.style.removeProperty('height');
            this.wrapper.style.removeProperty('width');
        }

        if(resetData)
        {
            this.data = getData(this.element, 'top' === this.options.position);
        }

        if(this.wrapper)
        {
            if (this.data.height)
            {
                this.wrapper.style.height = this.data.height + 'px';
            }

            this.wrapper.style.width = this.data.width + 'px';
        }
    }

    destroy()
    {
        if('0' === this.element.getAttribute('data-sticky-enabled'))
        {
            return;
        }

        this.reset(false);
        this.element.setAttribute('data-sticky-enabled', '0');
        window.removeEventListener('scroll', this.scrollWatcher);
        unwatchResize(this.resizeWatcher);
        stickies.splice(stickies.indexOf(this), 1);

        // rebuild currently unused
        // this.rebuild && this.vNode.context?.$off('v-sticky:rebuild', this.rebuild);
        // this.rebuild = null;

        let wrapper:HTMLElement = this.element.parentNode as HTMLElement;
        // remove wrapper if still in DOM
        if(wrapper && wrapper.parentNode)
        {
            wrapper.parentNode.insertBefore(this.element, wrapper);
            wrapper.parentNode.removeChild(wrapper);
        }
    }
}

const sticky: Directive = {
    created()
    {
        if (!supportSticky)
        {
            let tmp            = document.createElement('div');
            tmp.style.position = '-webkit-sticky';
            tmp.style.position = 'sticky';
            supportSticky      = tmp.style.position.includes('sticky');
        }
    },
    mounted: create,
    updated: create,
    unmounted: destroy,
};

export default sticky;